import type { PageContext } from '@zto/zpage'
import dayjs from 'dayjs'

/**
 * 新建 / 编辑 / 详情（
 * 门店基础信息，PRD §5.1）
 *
 * */
export function storeFormDialog(type: 'add' | 'edit' | 'view') {
  const isAdd = type === 'add'
  const isView = type === 'view'

  const actions = isView
    ? { close: {} }
    : {
        close: {},
        submit: { label: '确定' }
      }

  return {
    width: 720,
    labelWidth: 110,
    itemSpan: 24,
    onShow: async ({ data, app }: PageContext) => {
      if (isAdd) return

      const {
        provinceCode,
        cityCode,
        districtCode,
        province,
        city,
        district,
        gmtCreate,
        gmtModified,
        tagNames,
        isNewShop,
        ...other
      } = data || {}
      const pcd = [
        {
          provinceCode,
          cityCode,
          districtCode,
          province,
          city,
          district
        }
      ]
      return {
        payload: {
          pcd,
          isNewShopText: isNewShop === 1 ? '是' : '否',
          ...other
        }
      }
    },
    formItems: [
      {
        label: '所在地区',
        type: 'area-pro',
        prop: 'pcd',
        keyConfig: {
          provinceCode: 'provinceCode',
          province: 'province',
          cityCode: 'cityCode',
          city: 'city',
          districtCode: 'districtCode',
          district: 'district'
        },
        filterable: true,
        required: !isView,
        disabled: isView
      },
      {
        type: 'input',
        label: '详细地址',
        prop: 'detailAddress',
        maxlength: 100,
        trim: true,
        required: !isView,
        disabled: isView
      },
      {
        type: 'input',
        label: '门店编号',
        prop: 'shopCode',
        maxlength: 30,
        trim: true,
        required: isAdd,
        disabled: isView || !isAdd,
        rules: isAdd
          ? [
              {
                pattern: /^[A-Za-z0-9]+$/,
                message: '仅允许英文字母与数字'
              }
            ]
          : undefined
      },
      {
        type: 'select',
        label: '渠道',
        prop: 'channelCode',
        options: [{ label: '兔喜', value: 'TUXI' }],
        required: !isView,
        disabled: isView,
        clearable: false,
        default: 'TUXI'
      },
      {
        type: 'input',
        label: '门店名称',
        prop: 'shopName',
        maxlength: 30,
        trim: true,
        required: !isView,
        disabled: isView
      },
      {
        type: 'input',
        label: '联系人',
        prop: 'contactName',
        maxlength: 10,
        trim: true,
        disabled: isView
      },
      {
        type: 'input',
        label: '电话',
        prop: 'contactPhone',
        maxlength: 11,
        trim: true,
        disabled: isView,
        rules: [
          {
            pattern: /^$|^1[3-9]\d{9}$/,
            message: '请输入正确的电话号码'
          }
        ]
      },
      {
        type: 'input-number',
        label: '格口数',
        prop: 'gridCount',
        min: 0,
        max: 99999,
        precision: 0,
        disabled: isView
      },
      {
        type: 'input-number',
        label: '日包裹量',
        prop: 'dailyParcelEstimate',
        min: 0,
        max: 99999,
        precision: 0,
        disabled: isView
      },
      {
        type: 'text',
        label: '是否新门店',
        prop: 'isNewShopText',
        visibleOn: `${isView}`
      }
    ],
    beforeSubmit: async ({ data }: PageContext, model: any, options: any) => {
      if (isView) return false
      const { pcd, ...other } = data || {}
      const p0 = pcd?.[0] || {}
      return {
        ...other,
        ...p0
      }
    },
    actions
  }
}

/**
 * 单行维护标签：保存后为整组替换（PRD §6.2）
 *
 * */
export const maintainTagsDialog = {
  width: 520,
  labelWidth: 100,
  itemSpan: 24,
  title: '维护标签',

  onShow: async ({ data, app }: PageContext) => {
    const { tags } = data || {}
    return {
      payload: {
        ...data,
        tagCodes: tags?.map((item: any) => item.tagCode).filter(Boolean) || [],
        tagNames: tags?.map((item: any) => item.tagName).filter(Boolean) || []
      }
    }
  },

  formItems: [
    {
      type: 'fuzzy-select',
      prop: 'tagCodes',
      label: '门店标签',
      optionValueProp: 'tagCode',
      optionLabelProp: 'tagName',
      triggerFocus: true,
      multiple: true,
      multipleLimit: 20,
      remote: true,
      autoHeight: true,
      collapseTags: false,
      keywordProp: 'tagName',
      labelProp: 'tagNames',
      api: '/cabinet/admin/base/AdminBaseShopTagQueryController/page'
    }
  ],

  beforeSubmit: async ({ data }: PageContext, model: any, options: any) => {
    const { tagCodes, shopCode } = data || {}
    return {
      shopCodes: [shopCode],
      tagCodes
    }
  },
  actions: {
    close: {},
    submit: { label: '保存' }
  }
}

/** 批量打标：追加（PRD §6.2） */
export const batchAppendTagsDialog = {
  width: 520,
  labelWidth: 100,
  itemSpan: 24,
  title: '批量打标（追加）',
  formItems: [
    {
      type: 'html',
      label: '',
      labelWidth: 0,
      autoHeight: true,
      html: ({ data }: PageContext, ext: any) => {
        return `<div class="el-text el-text--info" style="margin-bottom:8px">已选 ${data.shopCodes?.length} 家门店。所选标签将追加到各门店已有标签上（不删除已有标签）。</div>`
      }
    },
    {
      type: 'fuzzy-select',
      prop: 'tagCodes',
      label: '门店标签',
      optionValueProp: 'tagCode',
      optionLabelProp: 'tagName',
      triggerFocus: true,
      multiple: true,
      multipleLimit: 20,
      remote: true,
      autoHeight: true,
      collapseTags: false,
      required: true,
      keywordProp: 'tagName',
      api: '/cabinet/admin/base/AdminBaseShopTagQueryController/page'
    }
  ],
  actions: {
    close: {},
    submit: { label: '确定' }
  }
}

/** 批量编辑标签：覆盖；未选表示清空（PRD §6.2，二次确认在接口侧或后续补充） */
export const batchReplaceTagsDialog = {
  width: 520,
  labelWidth: 100,
  itemSpan: 24,
  title: '批量编辑标签（覆盖）',
  formItems: [
    {
      type: 'html',
      label: '',
      labelWidth: 0,
      autoHeight: true,
      html: ({ data }: PageContext) => {
        return `<div class="el-text el-text--info" style="margin-bottom:8px">已选 ${data.shopCodes?.length} 家门店。下方所选标签将覆盖各门店当前全部标签；若不选任何标签并确认，表示将该批门店标签全部清空。</div>`
      }
    },
    {
      type: 'fuzzy-select',
      prop: 'tagCodes',
      label: '门店标签',
      optionValueProp: 'tagCode',
      optionLabelProp: 'tagName',
      triggerFocus: true,
      multiple: true,
      multipleLimit: 20,
      remote: true,
      autoHeight: true,
      collapseTags: false,
      keywordProp: 'tagName',
      api: '/cabinet/admin/base/AdminBaseShopTagQueryController/page'
    }
  ],
  actions: {
    close: {},
    submit: { label: '确定' }
  }
}



// 日志对话框
export function logDialog() {
  return {
    width: 900,
    top: '10vh',
    labelWidth: 0,
    itemSpan: 24,

    onShow: async ({ app, data }: PageContext) => {
      const res = await app.request({
        api: '/cabinet/admin/base/AdminBaseStoreExportLogQueryController/queryList',
        params: {
          pageIndex: 1,
          pageSize: 100
        }
      })

      return {
        payload: {
          operateLogData: res.data || []
        }
      }
    },

    formItems: [
      {
        label: '',
        labelWidth: 0,
        type: 'edit-table',
        prop: 'operateLogData',
        span: 24,
        noOperation: true,
        autoHeight: true,
        border: true,
        columns: [
          {
            label: '操作时间',
            minWidth: 150,
            formatter: (row: any) => {
              return row.gmtCreate ? row.gmtCreate : '--'
            }
          },
          { prop: 'operatorName', label: '操作人', minWidth: 100 },
          { prop: 'fileName', label: '操作类型', minWidth: 120 },
          { prop: 'exportCount', label: '导出门店条数', minWidth: 100 },
          { prop: 'remark', label: '备注', minWidth: 150 }
        ]
      }
    ],

    actions: {
      close: {}
    }
  }
}
